/**
 * Batch List Component
 * Displays list of batches with search and filter capabilities
 */

import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  inject,
  ChangeDetectionStrategy,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { PermissionService } from '@app/core/services/permission.service';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';
import { BatchService } from './services/batch.service';
import {
  Batch,
  BatchSearchParams,
  BatchStatus,
} from './models/batch.interface';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { SkuOptionsService } from '@shared/services/sku-options.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import {
  BATCH_SEARCH_CONFIG,
  BATCH_TABLE_CONFIG,
  BATCH_DEFAULT_CONFIG,
  BATCH_DEFAULT_COLUMNS_ORDER,
  BATCH_FLEXIBLE_COLUMNS_ORDER,
} from './batch.config';

@Component({
  selector: 'app-batch',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    TagModule,
    DialogModule,
    DatePickerModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './batch.component.html',
  styleUrl: './batch.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly batchService = inject(BatchService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly skuOptionsService = inject(SkuOptionsService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'batch' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.batch.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.batch.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.batch.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.batch.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.batch.approve');
  });

  // Configuration
  searchConfig = signal(this.buildSearchConfig());

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      BATCH_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of BATCH_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      BATCH_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of BATCH_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  pageSize = BATCH_DEFAULT_CONFIG.PAGE_SIZE;

  // State management
  batches = signal<Batch[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: BATCH_DEFAULT_CONFIG.PAGE_SIZE,
  });

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Search criteria
  private searchCriteria: BatchSearchParams = {};

  editExpiryVisible = false;
  editExpirySaving = false;
  editExpiryDate: Date | null = null;
  private editExpiryBatch: Batch | null = null;
  readonly minExpiryDate: Date = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d;
  })();

  ngOnInit(): void {
    this.loadData();
  }

  private buildSearchConfig(): FormFieldConfig[] {
    return withPaginatedSearchFields(BATCH_SEARCH_CONFIG, {
      skuCode: {
        loadPage: (params) => this.skuOptionsService.getOptionsPage(params),
      },
      warehouseId: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            status: true,
            valueKey: 'id',
          }),
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    const params: BatchSearchParams = {};

    // Only include non-empty values
    if (searchData['skuCode'] && (searchData['skuCode'] as string).trim()) {
      params.skuCode = searchData['skuCode'] as string;
    }

    if (searchData['status']) {
      params.status = searchData['status'] as BatchStatus;
    }

    if (searchData['warehouseId']) {
      params.warehouseId = searchData['warehouseId'] as number;
    }

    this.searchCriteria = params;
    this.searchParams.set({ ...this.searchParams(), page: 1 });
    this.loadData();
  }

  onReset(): void {
    this.searchCriteria = {};
    this.searchParams.set({
      page: 1,
      size: BATCH_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.loadData();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }

  onViewDetails(record: Batch): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    if (!record.id) {
      return;
    }
    this.router.navigate(['/stock/batch', record.id]);
  }

  openEditExpiry(record: Batch): void {
    if (!this.canUpdate() || !record.id) {
      return;
    }
    this.editExpiryBatch = record;
    const current =
      record.expiryDate instanceof Date
        ? record.expiryDate
        : record.expiryDate
          ? new Date(record.expiryDate)
          : null;
    this.editExpiryDate =
      current && !Number.isNaN(current.getTime()) ? current : null;
    this.editExpiryVisible = true;
  }

  closeEditExpiry(): void {
    this.editExpiryVisible = false;
    this.editExpirySaving = false;
    this.editExpiryBatch = null;
    this.editExpiryDate = null;
  }

  saveEditExpiry(): void {
    const batch = this.editExpiryBatch;
    if (!batch?.id || !this.editExpiryDate) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'batch_management.edit_expiry.invalid_date',
        ),
      });
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(this.editExpiryDate);
    selected.setHours(0, 0, 0, 0);
    if (selected.getTime() <= today.getTime()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'batch_management.edit_expiry.invalid_date',
        ),
      });
      return;
    }
    const yyyy = selected.getFullYear();
    const mm = String(selected.getMonth() + 1).padStart(2, '0');
    const dd = String(selected.getDate()).padStart(2, '0');
    this.editExpirySaving = true;
    this.batchService
      .updateExpiry(batch.id, `${yyyy}-${mm}-${dd}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'batch_management.messages.expiry_updated',
            ),
          });
          this.closeEditExpiry();
          this.loadData();
        },
        error: () => {
          this.editExpirySaving = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'batch_management.messages.failed_to_update',
            ),
          });
        },
      });
  }

  onToggleStatus(record: Batch): void {
    if (!this.canUpdate() || !record.id) {
      return;
    }
    const nextStatus: 'ACTIVE' | 'INACTIVE' =
      record.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    this.batchService
      .updateStatus(record.id, nextStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'batch_management.messages.status_updated',
            ),
          });
          this.loadData();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'batch_management.messages.failed_to_update',
            ),
          });
        },
      });
  }

  getStatusSeverity(
    status: BatchStatus,
  ): 'success' | 'danger' | 'info' | 'warning' | 'secondary' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'warning';
      case 'EXPIRED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  formatStatus(status: BatchStatus): string {
    return this.translateService.instant(
      `batch_management.status.${status.toLowerCase()}`,
    );
  }

  formatDate(date: string | Date | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('vi-VN');
  }

  private loadData(): void {
    this.loading.set(true);

    this.batchService
      .getBatches({
        ...this.searchCriteria,
        page: this.searchParams().page - 1, // Convert 1-based to 0-based for API
        size: this.searchParams().size,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.batches.set(response.content);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading batches:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'batch_management.messages.failed_to_load',
            ),
          });
          this.loading.set(false);
        },
      });
  }
}
